<!-- eslint-disable vue/no-restricted-html-elements -->
<template>
  <footer
    ref="footer"
    class="site-footer critical-component-clear"
  >
    <div class="container grid site-footer__grid">
      <div
        v-for="(block, index) of blocks"
        :key="index"
        class="col xs2-23"
        :class="{
          [getDesktopGridClass(index)]: true,
          'accordion-item': isMobile && (block.type !== 'socials' && block.type !== 'newsletter'),
          'socials-item': isMobile && block.type === 'socials'
        }"
      >
        <h2
          v-if="block.settings.title"
          class="site-footer__block-title text-body-m-regular"
          role="button"
          tabindex="0"
          :class="{
            'socials': block.type === 'socials',
            [$class('active')]: isMobile && activeIndex == index,
            'accordion-title':
              isMobile && (block.type !== 'socials' && block.type !== 'newsletter'),
            'font-family-freight': index === 0
          }"
          @click="isMobile && toggleAccordion(index)"
          v-text="block.settings.title"
        />

        <div
          v-if="block.type === 'newsletter'"
          class="site-footer__newsletter"
        >
          <div
            class="site-footer__newsletter-copy text-body-s-regular"
            v-html="$localizeRichtext(block.settings.copy)"
          />

          <klaviyo-sign-up-form
            :id="sectionId"
            :list-id="getListId(block)"
            :hightouch-source="block.settings.hightouch_source"
            :source="block.settings.source"
            :email-placeholder-text="block.settings.email_placeholder_text"
            :submit-text="block.settings.submit_text"
            :error-message="block.settings.error_message"
            :success-message="block.settings.success_message"
          />

          <div
            class="site-footer__newsletter-privacy-policy"
            v-html="$localizeRichtext(block.settings.privacy_policy_copy)"
          />
        </div>

        <div
          v-if="block.type === 'contact'"
          :ref="el => {accordionContents[index] = el}"
          :class="{
            'accordion-content': isMobile,
            [$class('open')]: isMobile && activeIndex == index
          }"
        >
          <ul class="site-footer__contact">
            <li v-if="block.settings.email">
              <span class="site-footer__contact-item">
                {{ $string('general.site_footer.contact.email') }}
                <a
                  :href="`mailto:${block.settings.email}`"
                  v-text="`${block.settings.email}`"
                />
              </span>
            </li>

            <li v-if="block.settings.phone">
              <span class="site-footer__contact-item">
                {{ $string('general.site_footer.contact.phone') }}
                <a
                  :href="`tel:${block.settings.phone}`"
                  v-text="`${block.settings.phone}`"
                />
              </span>
            </li>

            <li
              v-if="block.settings.hours"
              class="site-footer__hours"
            >
              <span
                class="site-footer__contact-item"
                v-text="`${$string('general.site_footer.contact.hours')} ${block.settings.hours}`"
              />

              <div
                v-if="openNow"
                class="site-footer__hours-open"
              >
                <span
                  class="visually-hidden"
                  v-text="$string('general.site_footer.contact.open')"
                />
              </div>
            </li>
          </ul>
        </div>

        <div
          v-if="block.type === 'menu'"
          :ref="el => {accordionContents[index] = el}"
          :class="{
            'accordion-content': isMobile,
            [$class('open')]: isMobile && activeIndex == index
          }"
        >
          <ul class="site-footer__menu">
            <li
              v-for="link of block.links"
              :key="link.title"
            >
              <a
                class="site-footer__menu-item"
                :aria-current="link.active"
                :href="$localizeUrl(link.url)"
                v-text="link.title"
              />
            </li>

            <li
              v-if="
                block.settings?.title?.toLowerCase() === 'legal' &&
                  onetrust !== ''
              "
            >
              <button
                id="ot-sdk-btn"
                class="
                  site-footer__menu-item ot-sdk-show-settings button button--text button--inline
                "
                type="button"
                v-text="$string('general.site_footer.cookies_settings')"
              />
            </li>
          </ul>
        </div>

        <div v-if="block.type === 'socials'">
          <ul class="site-footer__social-icons">
            <li
              v-for="social of filteredSocials(block)"
              :key="social"
            >
              <a
                :href="block.settings[`social_${social}_link`]"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  class="site-footer__social-icon"
                  :src="block.settings[`social_${social}_icon`]"
                  :alt="`${social} logo`"
                >
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div
        class="site-footer__copyright text-body-2xs-regular col xs2-23 l6-60"
        v-text="copyright"
      />
    </div>
  </footer>
</template>

<script>

/**
 * Vue: Site footer (site-footer)
 * -----------------------------------------------------------------------------
 * Site footer component with social links.
 *
 * @param {Array} blocks - Blocks of different types from section settings.
 * @param {String} copyright - Copyright string.
 * @param {String} sectionId - Section ID for passing into Klaviyo.
 */
import KlaviyoSignUpForm from '~async/klaviyo-sign-up-form/klaviyo-sign-up-form.vue'
import { debounce } from '~/helpers/general'
import { mapState } from 'vuex'

export default {
  name: 'SiteFooter',

  components: {
    KlaviyoSignUpForm,
  },

  props: {
    blocks: {
      type: Array,
      default: () => [],
    },
    copyright: {
      type: String,
      default: '',
    },
    sectionId: {
      type: String,
      default: '',
    },
    onetrust: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      cnvs: window.cnvs,
      footer: false,
      socials: [
        'facebook',
        'x',
        'instagram',
        'linkedin',
        'pinterest',
      ],
      activeIndex: null,
      accordionContents: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      settings: (state) => state.index.settings,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute whether current EST is within open business hours.
     * @returns {Boolean}
     */
    openNow() {
      const contactBlock = this.blocks.find(block => block.type === 'contact')

      if (!contactBlock ||
        !contactBlock.settings.open_time ||
        !contactBlock.settings.close_time
      ) {
        return false
      }

      const weekdayNow = new Date().toLocaleDateString('en-US', {
        timeZone: 'America/New_York',
        weekday: 'long',
      })

      if (weekdayNow === 'Saturday' || weekdayNow === 'Sunday') {
        return false
      }

      const hourNow = new Date().toLocaleTimeString('en-US', {
        timeZone: 'America/New_York',
        hour12: false,
      }).split(':')[0]

      return contactBlock.settings.open_time <= hourNow &&
        hourNow < contactBlock.settings.close_time
    },
  },

  mounted() {
    this.setEventListeners()

    this.footer = this.$refs.footer
    this.addScrollBounceClass()
  },

  methods: {

    /**
     * Set event listeners.
     */
    setEventListeners() {

      /**
       * Add default listeners.
       */
      window.visualViewport.addEventListener(
        'resize',
        debounce(() => this.addScrollBounceClass(), this.settings.throttle),
        { passive: true },
      )
    },

    /**
     * Array of social media links that have been merchandised.
     * @param {Object} block - Block from section settings
     * @returns {Array}
     */
    filteredSocials(block) {
      return this.socials.filter(social => this.hasSocial(block, social))
    },

    /**
     * Get desktop grid class based on block index.
     * @param {Number} index
     * @returns {String}
     */
    getDesktopGridClass(index) {
      if (index <= 1) {
        return 'l6-27'
      }

      if (index <= 3) {
        return 'l33-42'
      }

      return 'l48-60'
    },

    /**
     * Check if a certain social media link & icon is merchandised.
     * @param {Object} block - Block from section settings
     * @param {String} social - Social media platform name
     * @returns {Boolean}
     */
    hasSocial(block, social) {
      return block.settings[`social_${social}_link`] &&
        block.settings[`social_${social}_icon`]
    },

    /**
     * Adds/removes class to mitigate forced scroll on ios
     */
    addScrollBounceClass() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (
            entry.boundingClientRect.bottom <=
            window.innerHeight &&
            entry.intersectionRatio > 0
          ) {
            this.footer.classList.add('site-footer__hide-scroll')
          } else {
            this.footer.classList.remove('site-footer__hide-scroll')
          }
        })
      }, {
        threshold: [0, 1],
        rootMargin: '0px',
      })

      observer.observe(this.footer)
    },

    /**
     * Toogles the footer accordion
     */
    toggleAccordion(index) {
      const content = this.accordionContents[index]
      if (!content) {
        return
      }

      if (this.activeIndex === index) {
        content.style.maxHeight = null
        this.activeIndex = null
      } else {
        if (this.activeIndex !== null) {
          const prev = this.accordionContents[this.activeIndex]
          if (prev) {
            prev.style.maxHeight = null
          }
        }
        content.style.maxHeight = `${content.scrollHeight}px`
        this.activeIndex = index
      }
    },

    /**
     * Get list d
     * @param block
     */
    getListId(block) {
      if (cnvs?.store.country === 'US') {
        return block.settings.list_id
      }

      if (cnvs?.store.country === 'CA') {
        return block.settings.klaviyo_canada_list_id
      }

      return block.settings.international_list_id
    },
  },
}
</script>

<style lang="scss">
@import './site-footer';
@import '../../breadcrumbs/breadcrumbs';
</style>
